import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do medical practices in Colorado get more new patients online?",
    a: "Colorado medical practices grow new-patient volume through local SEO tuned to specific specialties (primary care, dermatology, orthopedics, cardiology, women's health), Google Business Profile optimization, and conversion-focused websites that lead with insurance accepted, new-patient forms, and online scheduling. We layer in MedicalBusiness and Physician schema so practices also surface inside AI Overviews and ChatGPT answers.",
  },
  {
    q: "How long does medical SEO take to work in Colorado?",
    a: "Most Colorado medical practices see meaningful Map Pack movement in 90–150 days and stronger organic patient acquisition by month 4–6. Specialty practices (orthopedics, dermatology, women's health) tend to compound faster because procedure-specific intent is highly searchable.",
  },
  {
    q: "Is medical marketing different from general healthcare marketing?",
    a: "Yes — medical marketing requires HIPAA-aware workflows, accurate provider/credential information, careful claims language, and content structured around real symptom and condition queries patients use. We never write content that implies guaranteed outcomes, and our review systems are HIPAA-safe by design.",
  },
  {
    q: "Do you handle Google Business Profile and reviews for medical practices?",
    a: "Yes — full GBP management is included in our Growth Marketing package, with provider profiles, weekly Posts, accurate hours and insurance, geo-tagged photos, and a HIPAA-compliant review-request workflow tied to visit completion in your EMR/practice software.",
  },
  {
    q: "How much does medical marketing cost in Colorado?",
    a: "Our Growth Marketing package is $1,650/month and includes SEO, Google Maps, GEO AI, and social — the full stack most Colorado practices need. Websites start at $1,850 one-time, owned outright. Owner-led, month-to-month, no long contracts.",
  },
];

export const Route = createFileRoute("/medical-marketing-colorado")({
  head: () =>
    industryMarketingHead("Medical", "medical practice", "medical-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Medical"
      industryLower="medical practice"
      slug="medical-marketing-colorado"
      subheadline="We help Colorado medical practices grow new-patient volume with local SEO, fast HIPAA-aware websites, Google Maps, and AI search visibility — without long contracts or generic healthcare templates."
      painPoints={[
        "New-patient calls have plateaued despite great providers and outcomes",
        "Hospital-system websites and national directories outrank you in Maps",
        "Generic agency content ignores your specialty, providers, and insurance mix",
      ]}
      serviceDescriptions={{
        seo: "Rank for specialty searches — primary care, dermatology, orthopedics, women's health — in every Colorado market you serve.",
        googleMaps: "Top-3 Map Pack visibility tuned to specialty + neighborhood + insurance intent.",
        webDesign: "Fast, mobile-first medical websites with insurance, new-patient forms, and online scheduling above the fold.",
        ppc: "Profitable Google Ads tuned to high-value services and the neighborhoods around your office.",
        geoAi: "Get cited when Colorado patients ask ChatGPT or Gemini 'best dermatologist near me' or 'top orthopedic clinic Denver'.",
        socialMedia: "Trust-building local content from real providers — not stock photos and generic health tips.",
      }}
      testimonial={{
        quote:
          "Within four months our online new-patient requests doubled, and we finally show up in Maps for the searches that matter.",
        role: "Practice Administrator, Multi-Provider Clinic",
        city: "Colorado Springs",
      }}
      faqs={FAQS}
      industrySpokes={[
        {
          to: "/medical-seo-colorado",
          label: "Medical SEO in Colorado",
          description: "Rank for new-patient searches across Colorado — primary care, specialties, and procedures.",
        },
      ]}
      cityHubLinks={[
        {
          to: "/colorado-springs",
          label: "Medical marketing in Colorado Springs",
          description: "Springs new-patient medical SEO and GBP from our local HQ.",
        },
        {
          to: "/denver-digital-marketing",
          label: "Medical marketing in Denver",
          description: "Compete with hospital systems in Denver's medical market with specialty-page SEO.",
        },
        {
          to: "/fort-collins-digital-marketing",
          label: "Medical marketing in Fort Collins",
          description: "Northern Colorado medical SEO and new-patient lead generation.",
        },
      ]}
    />
  ),
});
